import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import TierBadge, { TIER_STYLE } from '../components/TierBadge'

// FleetDashboard — fleet-wide rollup of diagnostic state across all sites.
// Designed for executives (Karen Hutton at ModWash, future enterprise customers).
// Everything reads from site_details + sites; no audit data needed.
//
// Layout:
//   1. Hero metrics row (surveyed %, verified %, total hardware $, fleet revenue)
//   2. Tier distribution (the "31/39/31/20" split that defines the engagement)
//   3. Camera mount distribution + Hardware investment breakdown
//   4. Top priority sites (drill-in by click)
//   5. Manual-entry watch list (operational risk)

const TIER_ORDER = ['critical','high','medium','low']

export default function FleetDashboard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [details, setDetails] = useState([])    // site_details rows joined with sites
  const [error, setError] = useState(null)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    // Single round trip — all the diagnostic columns we surface here.
    const { data, error } = await supabase
      .from('site_details')
      .select(`
        site_id, site_code, district, region,
        priority_tier, priority_rank, priority_score,
        camera_mount, gate_type, manual_lp_pct,
        weekly_net_sales, cars_washed_weekly,
        camera_update_cost, lpr_stand_update_cost,
        survey_verified, surveyor_name,
        sites!inner(id, name, city, state, active)
      `)
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }
    // Only count active sites — inactive shouldn't pollute the rollups.
    setDetails((data || []).filter(d => d.sites?.active !== false))
    setLoading(false)
  }

  const stats = useMemo(() => computeStats(details), [details])

  if (loading) return <div className="spinner" />
  if (error)   return <div className="empty-state"><h3>Couldn't load fleet data</h3><p>{error}</p></div>

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title">Fleet Dashboard</div>
          <div className="page-subtitle">
            {stats.totalSites} sites · diagnostic survey rollup
          </div>
        </div>
      </div>

      {/* Hero metrics row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 10, marginBottom: 14,
      }}>
        <HeroStat
          label="Surveyed"
          value={`${stats.surveyedSites}`}
          sub={`${pct(stats.surveyedSites, stats.totalSites)} of fleet`}
        />
        <HeroStat
          label="Verified"
          value={`${stats.verifiedSites}`}
          sub={`${pct(stats.verifiedSites, stats.surveyedSites)} of surveyed`}
          tone={stats.verifiedSites > 0 ? 'good' : 'neutral'}
        />
        <HeroStat
          label="Hardware investment"
          value={`$${stats.totalHardwareCost.toLocaleString()}`}
          sub="Estimated (camera + LPR)"
        />
        <HeroStat
          label="Fleet revenue"
          value={`$${(stats.totalWeeklyRevenue * 52 / 1_000_000).toFixed(1)}M`}
          sub={`$${stats.totalWeeklyRevenue.toLocaleString()}/wk`}
        />
      </div>

      {/* Tier distribution */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
          Priority tier distribution
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10,
        }}>
          {TIER_ORDER.map(tier => {
            const count = stats.tierCounts[tier] || 0
            const t = TIER_STYLE[tier]
            const sharePct = stats.surveyedSites > 0 ? (count / stats.surveyedSites) * 100 : 0
            return (
              <div key={tier}
                style={{
                  background: t.bg, color: t.color, padding: 12, borderRadius: 10,
                  cursor: 'pointer', transition: 'transform 0.1s ease',
                }}
                onClick={() => navigate(`/sites?tier=${tier}`)}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{ fontSize: 11, opacity: 0.8 }}>
                  {t.dot} {t.label}
                </div>
                <div style={{ fontSize: 26, fontWeight: 600, marginTop: 4, lineHeight: 1 }}>
                  {count}
                </div>
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                  {sharePct.toFixed(0)}% of surveyed
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Two-column row: camera mount + hardware investment */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginBottom: 12 }}>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Camera mount classification
          </div>
          <BreakdownBars
            items={[
              { label: 'On 4ft stand', value: stats.cameraMount.stand,    total: stats.cameraMountTotal, tone: 'bad' },
              { label: 'Overhead',     value: stats.cameraMount.overhead, total: stats.cameraMountTotal, tone: 'good' },
              { label: 'Mixed',        value: stats.cameraMount.mixed,    total: stats.cameraMountTotal, tone: 'warn' },
              { label: 'Other',        value: stats.cameraMount.other,    total: stats.cameraMountTotal, tone: 'neutral' },
            ]}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Sites with cameras on a 4ft stand strongly correlate with LPR failures.
            Relocating overhead is the single biggest hardware program.
          </div>
        </div>

        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Hardware investment breakdown
          </div>
          <CostRow label="LPR stand relocations" value={stats.totalLprStandCost} />
          <CostRow label="Camera updates"         value={stats.totalCameraCost} />
          <CostRow label="Total estimated"        value={stats.totalHardwareCost} bold />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Per-site estimates from the original NXT survey. Real costs may vary
            after on-site verification.
          </div>
        </div>
      </div>

      {/* Regional rollup — group sites by region, then break out each region's
          tier distribution + a few headline numbers. Karen will want to know
          'which regions have the most Critical sites?' the moment she sees
          the fleet-wide tier counts above. */}
      {stats.regionalGroups.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Regional rollup
          </div>
          {stats.regionalGroups.map(g => (
            <RegionalGroupRow key={g.region} g={g} navigate={navigate} />
          ))}
        </div>
      )}

      {/* Top priority sites — the "do these first" list */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Top priority sites
          </div>
          <button className="btn btn-sm" onClick={() => navigate('/sites')}>
            All sites →
          </button>
        </div>
        {stats.topSites.map(d => (
          <SiteRow key={d.site_id} d={d} onClick={() => navigate(`/sites/${d.site_id}`)} />
        ))}
      </div>

      {/* Manual-entry watch list */}
      {stats.highManualSites.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
            Manual entry watch list
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            {stats.highManualSites.length} sites with &gt;10% of plates entered manually.
            Each manual entry is friction at the kiosk and a missed LPR opportunity.
          </div>
          {stats.highManualSites.slice(0, 10).map(d => (
            <SiteRow key={d.site_id} d={d} onClick={() => navigate(`/sites/${d.site_id}`)}
              showManualPct />
          ))}
        </div>
      )}

      {/* Footer note */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 20 }}>
        Diagnostic data from initial survey. As sites are verified by NXT remote and
        MCWW field service, the verified count above ticks up.
      </div>
    </div>
  )
}

// ---- Components ----

function HeroStat({ label, value, sub, tone }) {
  const colorMap = {
    good:    { bg: '#EAF3DE', color: '#3B6D11' },
    bad:     { bg: '#FCEBEB', color: '#A32D2D' },
    warn:    { bg: '#FAEEDA', color: '#854F0B' },
    neutral: { bg: '#fff',     color: 'var(--text)' },
  }
  const c = colorMap[tone] || colorMap.neutral
  return (
    <div className="card" style={{ background: c.bg }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: c.color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function BreakdownBars({ items }) {
  const total = items.reduce((s, i) => s + (i.value || 0), 0)
  const toneColors = { good: '#3B6D11', bad: '#A32D2D', warn: '#854F0B', neutral: '#5F5E5A' }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {items.map(i => {
        const pct = total > 0 ? (i.value / total) * 100 : 0
        return (
          <div key={i.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
              <span>{i.label}</span>
              <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {i.value} ({pct.toFixed(0)}%)
              </span>
            </div>
            <div style={{ height: 8, background: '#F1EFE8', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`, height: '100%', background: toneColors[i.tone] || toneColors.neutral,
              }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function CostRow({ label, value, bold }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '8px 0',
      borderTop: '1px solid var(--border)', fontWeight: bold ? 600 : 400,
    }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>${(value || 0).toLocaleString()}</span>
    </div>
  )
}

function SiteRow({ d, onClick, showManualPct }) {
  const cameraStand = d.camera_mount === 'stand'
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 0', cursor: 'pointer',
        borderTop: '1px solid var(--border)',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
            {d.site_code}
          </span>
          <span style={{ fontWeight: 500, fontSize: 14 }}>{d.sites?.name}</span>
          <TierBadge tier={d.priority_tier} rank={d.priority_rank} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {[d.sites?.city, d.sites?.state].filter(Boolean).join(', ')}
          {d.district && ` · ${d.district}`}
          {cameraStand && <span style={{ color: '#A32D2D' }}> · ⚠️ stand cams</span>}
        </div>
      </div>
      {showManualPct && d.manual_lp_pct != null && (
        <span style={{
          fontSize: 13, fontWeight: 500, color: '#A32D2D',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {(Number(d.manual_lp_pct) * 100).toFixed(1)}%
        </span>
      )}
    </div>
  )
}

// Regional rollup row — one row per region, with expand-on-click for districts.
// Headline numbers are: total sites, Critical+High count (where the work is),
// Verified count (the metric Karen watches week-over-week). Tier mini-bar
// gives a visual share of each tier inside the region.
function RegionalGroupRow({ g, navigate }) {
  const [open, setOpen] = useState(false)
  const work = (g.tierCounts.critical || 0) + (g.tierCounts.high || 0)
  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
          flexWrap: 'wrap',
        }}>
        <div style={{ flex: '0 0 90px', fontWeight: 500, fontSize: 14 }}>
          Region {g.region}
          <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
            {open ? '▾' : '▸'}
          </span>
        </div>
        <TierMiniBar counts={g.tierCounts} />
        <div style={{ flex: '0 0 auto', display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          <span><strong style={{ color: 'var(--text)' }}>{g.totalSites}</strong> sites</span>
          {work > 0 && (
            <span><strong style={{ color: '#A32D2D' }}>{work}</strong> need work</span>
          )}
          <span>
            <strong style={{ color: g.verifiedSites > 0 ? '#3B6D11' : 'var(--text)' }}>
              {g.verifiedSites}
            </strong> verified
          </span>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 10, paddingLeft: 12, display: 'grid', gap: 6 }}>
          {g.districts.map(d => (
            <div key={d.district}
              onClick={() => navigate(`/sites?search=${encodeURIComponent(d.district)}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0',
                fontSize: 12, cursor: 'pointer',
              }}>
              <div style={{ flex: '0 0 130px', color: 'var(--text-muted)' }}>{d.district}</div>
              <TierMiniBar counts={d.tierCounts} small />
              <div style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
                {d.totalSites} sites
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Compact horizontal stacked bar showing tier shares inside a region/district.
function TierMiniBar({ counts, small }) {
  const total = (counts.critical || 0) + (counts.high || 0) + (counts.medium || 0) + (counts.low || 0)
  if (total === 0) return <div style={{ flex: 1 }} />
  const segs = [
    { tier: 'critical', color: '#A32D2D', count: counts.critical || 0 },
    { tier: 'high',     color: '#854F0B', count: counts.high     || 0 },
    { tier: 'medium',   color: '#3B6D11', count: counts.medium   || 0 },
    { tier: 'low',      color: '#5F5E5A', count: counts.low      || 0 },
  ]
  return (
    <div style={{
      flex: 1, height: small ? 6 : 10, borderRadius: 3, background: '#F1EFE8',
      overflow: 'hidden', display: 'flex', minWidth: 100, maxWidth: 240,
    }}>
      {segs.map(s => s.count > 0 && (
        <div key={s.tier}
          title={`${s.count} ${s.tier}`}
          style={{
            width: `${(s.count / total) * 100}%`, background: s.color,
          }} />
      ))}
    </div>
  )
}

// ---- Stats ----

function computeStats(details) {
  const tierCounts = { critical: 0, high: 0, medium: 0, low: 0 }
  const cameraMount = { stand: 0, overhead: 0, mixed: 0, other: 0 }
  let surveyedSites = 0
  let verifiedSites = 0
  let totalCameraCost = 0
  let totalLprStandCost = 0
  let totalWeeklyRevenue = 0

  for (const d of details) {
    if (d.priority_tier && tierCounts[d.priority_tier] != null) {
      tierCounts[d.priority_tier]++
      surveyedSites++
    }
    if (d.survey_verified) verifiedSites++
    if (d.camera_mount && cameraMount[d.camera_mount] != null) cameraMount[d.camera_mount]++
    if (d.camera_update_cost)    totalCameraCost   += Number(d.camera_update_cost)
    if (d.lpr_stand_update_cost) totalLprStandCost += Number(d.lpr_stand_update_cost)
    if (d.weekly_net_sales)      totalWeeklyRevenue += Number(d.weekly_net_sales)
  }

  // Top 10 priority sites — surveyed only, ranked by score desc
  const topSites = [...details]
    .filter(d => d.priority_score != null)
    .sort((a, b) => Number(b.priority_score) - Number(a.priority_score))
    .slice(0, 10)

  // Manual-entry watch list — sites above 10% manual entry, ranked by % desc
  const highManualSites = [...details]
    .filter(d => d.manual_lp_pct != null && Number(d.manual_lp_pct) > 0.10)
    .sort((a, b) => Number(b.manual_lp_pct) - Number(a.manual_lp_pct))

  const cameraMountTotal = Object.values(cameraMount).reduce((s, v) => s + v, 0)

  // Regional/district rollup. We bucket by the text fields region + district
  // (which are populated from the original survey) rather than by FK because
  // the legacy text fields are still authoritative for historical sites.
  // Sites with no region get bucketed under '(unassigned)' so they're visible
  // rather than silently dropped.
  const regionMap = new Map()  // region -> { tierCounts, verifiedSites, totalSites, districts: Map }
  for (const d of details) {
    const region = d.region || '(unassigned)'
    if (!regionMap.has(region)) {
      regionMap.set(region, {
        region,
        tierCounts: { critical: 0, high: 0, medium: 0, low: 0 },
        verifiedSites: 0,
        totalSites: 0,
        districts: new Map(),
      })
    }
    const r = regionMap.get(region)
    r.totalSites++
    if (d.priority_tier && r.tierCounts[d.priority_tier] != null) r.tierCounts[d.priority_tier]++
    if (d.survey_verified) r.verifiedSites++

    const district = d.district || '(unassigned)'
    if (!r.districts.has(district)) {
      r.districts.set(district, {
        district,
        tierCounts: { critical: 0, high: 0, medium: 0, low: 0 },
        totalSites: 0,
      })
    }
    const dEntry = r.districts.get(district)
    dEntry.totalSites++
    if (d.priority_tier && dEntry.tierCounts[d.priority_tier] != null) dEntry.tierCounts[d.priority_tier]++
  }

  // Convert to a sorted array — most Critical+High sites first (where the work is).
  const regionalGroups = [...regionMap.values()].map(r => ({
    ...r,
    districts: [...r.districts.values()].sort((a, b) => {
      const aWork = a.tierCounts.critical + a.tierCounts.high
      const bWork = b.tierCounts.critical + b.tierCounts.high
      return bWork - aWork || b.totalSites - a.totalSites
    }),
  })).sort((a, b) => {
    const aWork = a.tierCounts.critical + a.tierCounts.high
    const bWork = b.tierCounts.critical + b.tierCounts.high
    return bWork - aWork || b.totalSites - a.totalSites
  })

  return {
    totalSites: details.length,
    surveyedSites,
    verifiedSites,
    tierCounts,
    cameraMount,
    cameraMountTotal,
    totalCameraCost,
    totalLprStandCost,
    totalHardwareCost: totalCameraCost + totalLprStandCost,
    totalWeeklyRevenue,
    topSites,
    highManualSites,
    regionalGroups,
  }
}

function pct(num, denom) {
  if (!denom) return '0%'
  return `${Math.round((num / denom) * 100)}%`
}
